/**
 * Tarjeta de formulario: muestra un formulario con sus datos, estado
 * (completado/pendiente) y acciones (editar, eliminar, marcar completado).
 */
import { useState, useEffect, useRef } from 'react';
import { Car, Bike, Truck, Bus, FileText, Copy, Pencil, Trash2, User, PersonStanding, Caravan, Check, Baby, ChevronDown, ChevronUp, IdCard, Ticket, StickyNote } from 'lucide-react';
import styles from './FormCard.module.css';

interface FormCardProps {
    item: {
        id: number;
        datos: {
            vehiculos?: { coches?: string; motos?: string; camiones?: string; autobuses?: string; remolques?: string };
            pasajeros?: { adultos?: string; niños?: string; bebes?: string };
            numeroCoche?: string;
            tipoVehiculo?: string;
            cantidadVehiculos?: string;
            remolques?: string;
            codigoReservas: string[];
            anotaciones?: string;
            embarqueId: string;
        };
        completado: boolean;
        completado_secundario?: boolean;
        fecha_creacion: string;
        usuario_nombre?: string;
        usuario_username?: string;
    };
    onDelete: (id: number) => void;
    onToggleStatus: (id: number, currentStatus: boolean) => void;
    onToggleSecondaryStatus: (id: number, currentStatus: boolean) => void;
    onEdit: (item: FormCardProps['item']) => void;
    forceExpand?: boolean;
}
/** Icono de control completado en cabecera (sin caja de fondo). */
const HEADER_STATUS_ICON_DONE = '#22c55e';
/** Iconos cabecera derecha (original ~22px). Chevron algo menor, como antes. */
const HEADER_STATUS_ICON_PX = 22;
const HEADER_CHEVRON_PX = 20;
const HEADER_VEHICLE_PRIMARY_PX = 28;
const HEADER_VEHICLE_SECONDARY_PX = 19;

function vehiculoAriaLabel(tipo: string, n: number): string {
    const labels: Record<string, [string, string]> = {
        coche: ['coche', 'coches'],
        moto: ['moto', 'motos'],
        camion: ['camión', 'camiones'],
        autobus: ['autobús', 'autobuses'],
        remolque: ['remolque', 'remolques'],
    };
    const pair = labels[tipo];
    if (!pair) return `${n} vehículos`;
    const [s, p] = pair;
    return n === 1 ? `1 ${s}` : `${n} ${p}`;
}

export default function FormCard({
    item,
    onDelete,
    onToggleStatus,
    onToggleSecondaryStatus,
    onEdit,
    forceExpand = false,
}: FormCardProps) {
    const [isCollapsed, setIsCollapsed] = useState(!forceExpand);
    const [copiedReservaIndex, setCopiedReservaIndex] = useState<number | null>(null);
    const [copiedAnotaciones, setCopiedAnotaciones] = useState(false);
    const copyResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const annotCopyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { datos, completado, completado_secundario: completadoSecRaw, id } = item;
    const completadoSecundario = completadoSecRaw === true;

    useEffect(() => {
        setIsCollapsed(!forceExpand);
    }, [forceExpand]);

    useEffect(() => () => {
        if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
        if (annotCopyTimerRef.current) clearTimeout(annotCopyTimerRef.current);
    }, []);

    const copyToClipboard = (text: string, lineIndex: number) => {
        navigator.clipboard.writeText(text).then(() => {
            if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
            if (annotCopyTimerRef.current) {
                clearTimeout(annotCopyTimerRef.current);
                annotCopyTimerRef.current = null;
            }
            setCopiedAnotaciones(false);
            setCopiedReservaIndex(lineIndex);
            copyResetTimerRef.current = setTimeout(() => {
                setCopiedReservaIndex(null);
                copyResetTimerRef.current = null;
            }, 2000);
        }).catch(() => {});
    };

    const copyAnotacionesToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            if (annotCopyTimerRef.current) clearTimeout(annotCopyTimerRef.current);
            if (copyResetTimerRef.current) {
                clearTimeout(copyResetTimerRef.current);
                copyResetTimerRef.current = null;
            }
            setCopiedReservaIndex(null);
            setCopiedAnotaciones(true);
            annotCopyTimerRef.current = setTimeout(() => {
                setCopiedAnotaciones(false);
                annotCopyTimerRef.current = null;
            }, 2000);
        }).catch(() => {});
    };

    const vCounts = (() => {
        if (datos.vehiculos) return datos.vehiculos;
        const legacy: Record<string, string> = { coches: '0', motos: '0', camiones: '0', autobuses: '0', remolques: '0' };
        const tipo = datos.tipoVehiculo || 'coche';
        const cant = datos.cantidadVehiculos || '1';
        if (tipo === 'moto') legacy.motos = cant;
        else if (tipo === 'camion') legacy.camiones = cant;
        else if (tipo === 'autobus') legacy.autobuses = cant;
        else if (tipo === 'remolque') legacy.remolques = cant;
        else legacy.coches = cant;
        if (datos.remolques && datos.remolques !== '0') legacy.remolques = datos.remolques;
        return legacy;
    })();

    const vehicleBadges: { icon: (color: string, size: number) => React.ReactNode; tipo: string; count: number }[] = [
        { icon: (c: string, s: number) => <Car size={s} color={c} />, tipo: 'coche', count: parseInt(vCounts.coches || '0', 10) },
        { icon: (c: string, s: number) => <Bike size={s} color={c} />, tipo: 'moto', count: parseInt(vCounts.motos || '0', 10) },
        { icon: (c: string, s: number) => <Truck size={s} color={c} />, tipo: 'camion', count: parseInt(vCounts.camiones || '0', 10) },
        { icon: (c: string, s: number) => <Bus size={s} color={c} />, tipo: 'autobus', count: parseInt(vCounts.autobuses || '0', 10) },
        { icon: (c: string, s: number) => <Caravan size={s} color={c} />, tipo: 'remolque', count: parseInt(vCounts.remolques || '0', 10) },
    ].filter(b => b.count > 0);

    const tipoKey = (datos.tipoVehiculo || 'coche').toLowerCase();
    const hasAnyVehicle = vehicleBadges.length > 0;
    const hasAnyPassenger =
        parseInt(datos.pasajeros?.adultos || '0', 10) +
        parseInt(datos.pasajeros?.niños || '0', 10) +
        parseInt(datos.pasajeros?.bebes || '0', 10) > 0;
    const primaryBadge = vehicleBadges.find(b => b.tipo === tipoKey) ?? vehicleBadges[0];
    const secondaryBadge = vehicleBadges.length >= 2
        ? vehicleBadges.find(b => b !== primaryBadge)
        : undefined;

    const idHeader = datos.numeroCoche?.trim() || '';

    return (
        <div className={styles.card}>
            <div className={styles.header}>
                <button
                    type="button"
                    className={styles.headerLeft}
                    onClick={() => setIsCollapsed(prev => !prev)}
                    aria-expanded={!isCollapsed}
                    aria-label={
                        idHeader
                            ? `${isCollapsed ? 'Expandir' : 'Contraer'} ficha, ID ${idHeader}`
                            : hasAnyVehicle
                              ? isCollapsed
                                ? 'Expandir ficha'
                                : 'Contraer ficha'
                              : isCollapsed
                                ? 'Expandir ficha (sin vehículo, solo pasajeros)'
                                : 'Contraer ficha (sin vehículo, solo pasajeros)'
                    }
                >
                    {hasAnyVehicle && primaryBadge ? (
                        <div className={styles.vehicleStack} aria-hidden>
                            {secondaryBadge && (
                                <span className={styles.vehicleStackBack}>{secondaryBadge.icon('rgba(255,255,255,0.45)', HEADER_VEHICLE_SECONDARY_PX)}</span>
                            )}
                            <span className={styles.vehicleStackFront}>{primaryBadge.icon('white', HEADER_VEHICLE_PRIMARY_PX)}</span>
                        </div>
                    ) : hasAnyPassenger ? (
                        <div className={styles.vehicleStack} aria-hidden>
                            <span className={styles.vehicleStackFront}>
                                <User size={HEADER_VEHICLE_PRIMARY_PX} color="white" strokeWidth={2} />
                            </span>
                        </div>
                    ) : null}
                    {idHeader && (hasAnyVehicle || hasAnyPassenger) ? (
                        <div className={styles.headerLeftDivider} aria-hidden />
                    ) : null}
                    {idHeader ? (
                        <span className={styles.headerIdBlock}>
                            <span className={styles.headerIdLabel}>ID:</span>
                            <span className={styles.headerNumber}>{idHeader}</span>
                        </span>
                    ) : null}
                </button>

                <div className={styles.headerRight}>
                    <div className={styles.actionsPill}>
                        <button
                            type="button"
                            className={styles.pillIconButton}
                            onClick={e => {
                                e.stopPropagation();
                                onToggleStatus(id, completado);
                            }}
                            aria-label={
                                completado
                                    ? 'Marcar control de documentación como pendiente'
                                    : 'Marcar control de documentación como completado'
                            }
                        >
                            {completado ? (
                                <Check size={HEADER_STATUS_ICON_PX} color={HEADER_STATUS_ICON_DONE} strokeWidth={2.5} />
                            ) : (
                                <IdCard size={HEADER_STATUS_ICON_PX} color="currentColor" strokeWidth={2} />
                            )}
                        </button>

                        <button
                            type="button"
                            className={styles.pillIconButton}
                            onClick={e => {
                                e.stopPropagation();
                                onToggleSecondaryStatus(id, completadoSecundario);
                            }}
                            aria-label={
                                completadoSecundario
                                    ? 'Marcar control de billetes como pendiente'
                                    : 'Marcar control de billetes como completado'
                            }
                        >
                            {completadoSecundario ? (
                                <Check size={HEADER_STATUS_ICON_PX} color={HEADER_STATUS_ICON_DONE} strokeWidth={2.5} />
                            ) : (
                                <Ticket size={HEADER_STATUS_ICON_PX} color="currentColor" strokeWidth={2} />
                            )}
                        </button>
                    </div>

                    <button
                        type="button"
                        className={styles.headerChevronBtn}
                        onClick={e => {
                            e.stopPropagation();
                            setIsCollapsed(prev => !prev);
                        }}
                        aria-expanded={!isCollapsed}
                        aria-label={isCollapsed ? 'Expandir ficha' : 'Contraer ficha'}
                    >
                        {isCollapsed ? (
                            <ChevronDown size={HEADER_CHEVRON_PX} color="currentColor" strokeWidth={2.25} aria-hidden />
                        ) : (
                            <ChevronUp size={HEADER_CHEVRON_PX} color="currentColor" strokeWidth={2.25} aria-hidden />
                        )}
                    </button>
                </div>
            </div>

            {!isCollapsed && (
                <div className={styles.body}>
                    <div className={styles.bodyCountsRow}>
                        {vehicleBadges.map((b, i) => (
                            <div key={`v-${i}`} className={styles.countIconSlot} aria-label={vehiculoAriaLabel(b.tipo, b.count)}>
                                <span aria-hidden className={styles.countIconInner}>{b.icon('#002B45', 20)}</span>
                                <span className={styles.countBadge}>{b.count}</span>
                            </div>
                        ))}
                        {datos.pasajeros?.adultos && datos.pasajeros.adultos !== '0' && (
                            <div className={styles.countIconSlot} aria-label={`${datos.pasajeros.adultos} adultos`}>
                                <span aria-hidden className={styles.countIconInner}><User size={20} color="#002B45" /></span>
                                <span className={styles.countBadge}>{datos.pasajeros.adultos}</span>
                            </div>
                        )}
                        {datos.pasajeros?.niños && datos.pasajeros.niños !== '0' && (
                            <div className={styles.countIconSlot} aria-label={`${datos.pasajeros.niños} niños`}>
                                <span aria-hidden className={styles.countIconInner}><PersonStanding size={20} color="#002B45" /></span>
                                <span className={styles.countBadge}>{datos.pasajeros.niños}</span>
                            </div>
                        )}
                        {datos.pasajeros?.bebes && datos.pasajeros.bebes !== '0' && (
                            <div className={styles.countIconSlot} aria-label={`${datos.pasajeros.bebes} bebés`}>
                                <span aria-hidden className={styles.countIconInner}><Baby size={20} color="#002B45" /></span>
                                <span className={styles.countBadge}>{datos.pasajeros.bebes}</span>
                            </div>
                        )}
                    </div>

                    {datos.codigoReservas?.map((reserva, index) => (
                        reserva.trim() !== "" && (
                            <div key={index} className={styles.detailBlock}>
                                <div className={styles.detailLabelRow}>
                                    <FileText size={16} color="currentColor" />
                                    <span className={styles.detailLabel}>Reserva {datos.codigoReservas.length > 1 ? index + 1 : ''}:</span>
                                </div>
                                <div className={styles.detailValueBox}>
                                    <span className={styles.detailValue}>{reserva}</span>
                                    <div className={styles.detailDivider} />
                                    <button
                                        type="button"
                                        className={styles.detailCopyBtn}
                                        onClick={() => copyToClipboard(reserva, index)}
                                        aria-label={copiedReservaIndex === index ? 'Copiado' : 'Copiar al portapapeles'}
                                    >
                                        {copiedReservaIndex === index ? (
                                            <Check size={18} color="#16a34a" strokeWidth={2.5} className={styles.copyCheck} aria-hidden />
                                        ) : (
                                            <Copy size={16} color="#002B45" aria-hidden />
                                        )}
                                    </button>
                                </div>
                            </div>
                        )
                    ))}

                    {datos.anotaciones?.trim() ? (
                        <div className={styles.detailBlock}>
                            <div className={styles.detailLabelRow}>
                                <StickyNote size={16} color="currentColor" />
                                <span className={styles.detailLabel}>Anotaciones</span>
                            </div>
                            <div className={`${styles.detailValueBox} ${styles.detailValueBoxAnnot}`}>
                                <span className={styles.detailValue}>{datos.anotaciones.trim()}</span>
                                <button
                                    type="button"
                                    className={styles.detailCopyBtn}
                                    onClick={() => copyAnotacionesToClipboard(datos.anotaciones!.trim())}
                                    aria-label={copiedAnotaciones ? 'Copiado' : 'Copiar anotaciones al portapapeles'}
                                >
                                    {copiedAnotaciones ? (
                                        <Check size={18} color="#16a34a" strokeWidth={2.5} className={styles.copyCheck} aria-hidden />
                                    ) : (
                                        <Copy size={16} color="#002B45" aria-hidden />
                                    )}
                                </button>
                            </div>
                        </div>
                    ) : null}

                    <div className={styles.actions}>
                        <button
                            type="button"
                            className={`${styles.actionButton} ${styles.actionBtnPrimary}`}
                            onClick={() => onEdit(item)}
                        >
                            <Pencil size={16} color="currentColor" strokeWidth={2} aria-hidden />
                            Editar
                        </button>
                        <button
                            type="button"
                            className={`${styles.actionButton} ${styles.actionBtnDanger}`}
                            onClick={() => onDelete(id)}
                        >
                            <Trash2 size={16} color="currentColor" strokeWidth={2} aria-hidden />
                            Borrar
                        </button>
                    </div>

                </div>
            )}
        </div>
    );
}
